import type { Command } from "../document/commands.ts";
import type { WorldDocument } from "../document/types.ts";
import { planLayout, readLayoutPlan, type LayoutAnswer, type LayoutPlan, type LayoutReport } from "./layout-plan.ts";

/**
 * Apply world layout on the store (ADR-109 decisions 14 and 15, P1.6-T142): the plan's preview, which is the
 * report of the commands `planLayout` would run over the world as it is now, and the apply, which runs those
 * commands as one compound command through the store's own `run`, so there is one history entry and one undo,
 * and with a session the one edit travels as operations in frames of at most `OP_INTENTS_MAX` intents under the
 * session's pace. A plan is read and checked by `readLayoutPlan`; who may apply one is the host's to decide
 * (the account's own permission), as the server decides it for the operations.
 */

/**
 * Reads a plan file's text.
 *
 * @param text The text of the file the builder chose.
 * @returns The plan, read and checked by `readLayoutPlan`.
 * @throws Error "The file is not JSON" for text that is not, else `readLayoutPlan`'s words naming the line.
 */
export function readLayoutPlanText(text: string): LayoutPlan {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error("The file is not JSON");
  }
  return readLayoutPlan(value);
}

/**
 * What the apply needs from the store.
 */
export interface LayoutApplyHost {
  readonly document: () => WorldDocument;
  readonly forget: (mapId: string) => void;
  readonly run: (command: Command) => boolean;
  /** The tag the tab mints its ids under, or null for an editor with none. */
  readonly idTag: () => string | null;
}

/**
 * The store's world layout.
 */
export class LayoutApply {
  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: LayoutApplyHost) {}

  /**
   * What applying a plan would do to the world as it is now, with nothing recorded and nothing changed.
   *
   * @param plan The plan.
   * @returns The commands and the report.
   * @throws Error naming the plan's line when the plan breaks a rule of itself or of the world.
   */
  public plan(plan: LayoutPlan): LayoutAnswer {
    return planLayout(this.host.document(), plan, { idTag: this.host.idTag() });
  }

  /**
   * Applies a plan as one compound command, planned again on the world as it is at this moment, so a change
   * another builder made since the preview is met as a second apply meets it.
   *
   * @param plan The plan.
   * @returns The report of what was applied, or null when the world already holds the layout and nothing was
   *   recorded.
   * @throws Error naming the plan's line when the plan does not apply, or saying the layout was not applied
   *   when the store or its session refused the command.
   */
  public apply(plan: LayoutPlan): LayoutReport | null {
    const answer = this.plan(plan);
    if (answer.commands.length === 0) {
      return null;
    }
    for (const added of answer.report.added) {
      this.host.forget(added.id);
    }
    if (!this.host.run({ kind: "compound", commands: answer.commands })) {
      throw new Error("the layout was not applied");
    }
    return answer.report;
  }
}
